// ─── Region / Sub-region config ───────────────────────────────────────────────
// Edit the maps below to change how market_area (and segment_sales for AMER US)
// drive the region and sub_region fields on every deal.
//
// sub_region: null  →  the sub_region is resolved from segment_sales instead
//                       (used for United States where segment drives the split).

export interface RegionEntry {
  region: string;
  sub_region: string | null;
}

export const MARKET_AREA_REGION_MAP: Record<string, RegionEntry> = {
  // ── AMER ────────────────────────────────────────────────────────────────────
  'United States':        { region: 'AMER', sub_region: null },   // → resolved via segment OR segment_sales in order of preference
  'Canada':               { region: 'AMER', sub_region: 'CANADA' },
  'Strat. Latin America': { region: 'AMER', sub_region: 'LATAM' },
  'Brazil':               { region: 'AMER', sub_region: 'LATAM' },
  'Mexico':               { region: 'AMER', sub_region: 'LATAM' },

  // ── EMEA ────────────────────────────────────────────────────────────────────
  'United Kingdom': { region: 'EMEA', sub_region: 'UKI' },
  'SSA & Israel':   { region: 'EMEA', sub_region: 'MEA' },
  'Middle East':    { region: 'EMEA', sub_region: 'MEA' },
  'Germany':        { region: 'EMEA', sub_region: 'CENTRAL' },
  'Austria':        { region: 'EMEA', sub_region: 'CENTRAL' },
  'Switzerland':        { region: 'EMEA', sub_region: 'CENTRAL' },
  'Bulgaria':        { region: 'EMEA', sub_region: 'CENTRAL' },
  'Czech Republic':        { region: 'EMEA', sub_region: 'CENTRAL' },
  'Estonia':        { region: 'EMEA', sub_region: 'CENTRAL' },
  'Greece':        { region: 'EMEA', sub_region: 'CENTRAL' },
  'Hungary':        { region: 'EMEA', sub_region: 'CENTRAL' },
  'Poland':        { region: 'EMEA', sub_region: 'CENTRAL' },
  'Romania':        { region: 'EMEA', sub_region: 'CENTRAL' },
  'Russia':        { region: 'EMEA', sub_region: 'CENTRAL' },
  'Slovenia':        { region: 'EMEA', sub_region: 'CENTRAL' },
  'Slovakia':        { region: 'EMEA', sub_region: 'CENTRAL' },
  'Turkey':        { region: 'EMEA', sub_region: 'CENTRAL' },
  'Ukraine':        { region: 'EMEA', sub_region: 'CENTRAL' },

  'Belgium':          { region: 'EMEA', sub_region: 'SOUTH WESTERN' },
  'Denmark':          { region: 'EMEA', sub_region: 'SOUTH WESTERN' },
  'Spain':          { region: 'EMEA', sub_region: 'SOUTH WESTERN' },
  'Finland':          { region: 'EMEA', sub_region: 'SOUTH WESTERN' },
  'France':          { region: 'EMEA', sub_region: 'SOUTH WESTERN' },
  'Italy':          { region: 'EMEA', sub_region: 'SOUTH WESTERN' },
  'Lithuania':          { region: 'EMEA', sub_region: 'SOUTH WESTERN' },
  'Latvia':          { region: 'EMEA', sub_region: 'SOUTH WESTERN' },
  'Luxembourg':          { region: 'EMEA', sub_region: 'SOUTH WESTERN' },
  'Netherlands':          { region: 'EMEA', sub_region: 'SOUTH WESTERN' },
  'Norway':          { region: 'EMEA', sub_region: 'SOUTH WESTERN' },
  'Portugal':          { region: 'EMEA', sub_region: 'SOUTH WESTERN' },
  'Sweden':          { region: 'EMEA', sub_region: 'SOUTH WESTERN' },
  'Nordic':         { region: 'EMEA', sub_region: 'SOUTH WESTERN' },

  'Iberica':        { region: 'EMEA', sub_region: 'OTHERS' },
  'Benelux':        { region: 'EMEA', sub_region: 'OTHERS' },
  'Eastern Europe': { region: 'EMEA', sub_region: 'OTHERS' },

  // ── JAPAC ───────────────────────────────────────────────────────────────────
  'ANZ':    { region: 'JAPAC', sub_region: 'ANZ' },
  'Japan':  { region: 'JAPAC', sub_region: 'JAPAN' },
  'SEA':    { region: 'JAPAC', sub_region: 'SEA' },
  'Korea':  { region: 'JAPAC', sub_region: 'OTHERS' },
  'India':  { region: 'JAPAC', sub_region: 'OTHERS' },
};

// Segment → sub_region for AMER deals where sub_region is null (i.e. United States).
export const AMER_SEGMENT_SUBREGION: Record<string, string> = {
  'Industry':   'IND',
  'Enterprise': 'ENT',
  'Scale':      'SCALE'
};

export const AMER_SEGMENT_SUBREGION_DEFAULT = 'OTHERS';

export function resolveRegion(
  market_area: string | undefined,
  segment_sales: string | undefined,
  segment: string | undefined
): { region: string; sub_region: string } | null {

  if (!market_area) return null;
  
  const entry = MARKET_AREA_REGION_MAP[market_area];
  if (!entry) return null;

  const sub_region =
    entry.sub_region ??
    AMER_SEGMENT_SUBREGION[segment ?? ''] ??
    AMER_SEGMENT_SUBREGION[segment_sales ?? ''] ??
    AMER_SEGMENT_SUBREGION_DEFAULT;

  return { region: entry.region, sub_region };
}
